import { usePaymentGroups } from '@/modules/common/hooks/data-hooks/usePaymentGroups';
import { PaymentStatus } from '@/types/payment';
import { IPaymentGroup } from '@/types/payment-groups';
import { format } from 'date-fns';
import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';

export const useExportPaymentGroups = (filters?: {
  status?: PaymentStatus | PaymentStatus[];
  stage?: string;
}) => {
  const [shouldFetchAllPaymentGroups, setShouldFetchAllPaymentGroups] =
    useState(false);

  const { refetch: refetchAllPaymentGroups } = usePaymentGroups({
    filters: {
      ...filters,
    },
    pagination: {
      page: 1,
      limit: 999999, // Fetch all payment groups
    },
    sort: {
      createdAt: 'desc',
    },
    options: {
      enabled: shouldFetchAllPaymentGroups,
    },
  });

  const exportPaymentGroups = async () => {
    setShouldFetchAllPaymentGroups(true);

    const result = await refetchAllPaymentGroups();

    if (!result.data?.data?.length) {
      enqueueSnackbar('No payment groups to export', {
        variant: 'error',
      });
      setShouldFetchAllPaymentGroups(false);
      return;
    }

    const allPaymentGroups = result.data.data;

    const headers = [
      'ID',
      'Payment Date',
      'Number of Payments',
      'Payment Method',
      'Amount',
      'Currency',
      'Status',
      'Stage',
    ];

    const csv = `${headers.join(',')}\n`;

    const lines = allPaymentGroups.map((paymentGroup: IPaymentGroup) =>
      [
        paymentGroup._id,
        format(new Date(paymentGroup.createdAt), 'Pp'),
        paymentGroup.payments.length,
        paymentGroup.method,
        paymentGroup.amount,
        paymentGroup.currency,
        paymentGroup.status,
        paymentGroup.stage || 'N/A',
      ].join(','),
    );

    // Generate filename with stage information
    const stageSuffix = filters?.stage
      ? `-${filters.stage.replace('_', '-')}`
      : '';
    const filename = `payment-groups${stageSuffix}-${Date.now()}.csv`;

    const blob = new Blob([csv.concat(lines.join('\n'))], {
      type: 'text/csv;charset=utf-8;',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setShouldFetchAllPaymentGroups(false);

    enqueueSnackbar('Payment groups successfully exported', {
      variant: 'success',
    });
  };

  return {
    exportPaymentGroups,
    isExporting: shouldFetchAllPaymentGroups,
  };
};
